import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { ManagePublicationPaginationRuleParams } from '@hooks/usePublicationPaginationRule'

import type { PublicationPaginationRuleModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/publicationPaginationRule/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/publicationPaginationRule/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/publicationPaginationRule/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/publicationPaginationRule/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/publicationPaginationRule/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/publicationPaginationRule/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/publicationPaginationRule/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/publicationPaginationRule/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/publicationPaginationRule/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/publicationPaginationRule/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/publicationPaginationRule/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/publicationPaginationRule/DELETE_FAILURE'

export type PublicationPaginationRuleModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: Errors,
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: PublicationPaginationRuleModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadPublicationPaginationRules(options: EntityOptions = {}) {
  const { entityKey } = options || {}

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().publicationPaginationRules.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/publication_pagination_rules.json', options)
      .then(({ data }: { data: PublicationPaginationRuleModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_PAGINATION_RULE_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createPublicationPaginationRule(
  publicationPaginationRule: Partial<PublicationPaginationRuleModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      publication_pagination_rule: publicationPaginationRule,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/publication_pagination_rules.json', options, config)
      .then(({ data }: { data: PublicationPaginationRuleModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_PAGINATION_RULE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function manageForPaginationRule(
  params: ManagePublicationPaginationRuleParams,
  options: EntityOptions = {},
) {
  const {
    action, pagination_rule_id, provider_id, publication_ids, string,
  } = params

  const config = {
    method: 'POST',
    data: JSON.stringify({
      action_key: action,
      pagination_rule_id,
      provider_id,
      publication_ids,
      string,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/publication_pagination_rules/manage_for_pagination_rule.json', options, config)
      .then(({ data }: { data: PublicationPaginationRuleModel[] }) => {
        if (action === 'assign'){
          const normalizedJson = normalize(data, Schemas.PUBLICATION_PAGINATION_RULE_ARRAY)
          // @ts-expect-error TS2345
          dispatch(updateEntities(normalizedJson))
        } else {
          data.forEach((agencyOrderBundle) => {
            const normalizedJson = normalize(agencyOrderBundle, Schemas.PUBLICATION_PAGINATION_RULE)
            // @ts-expect-error TS2345
            dispatch(deleteEntity(normalizedJson))
          })
        }

        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deletePublicationPaginationRule(
  publicationPaginationRule: DeleteParams<PublicationPaginationRuleModel>,
) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/publication_pagination_rules/${publicationPaginationRule.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_PAGINATION_RULE)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: PublicationPaginationRuleModuleState = initialState,
  action: Action = { type: '' },
): PublicationPaginationRuleModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
